import { Button, Icon, Typography } from '@/components/common';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CampaignChannels, CampaignTabs, Option } from '@/interfaces';
import clsx from 'clsx';
import * as React from 'react';
import { InputCheck } from './input-check';
import { useForm } from 'react-hook-form';
import { campaignPlatformLabelMap, getAllAdsArray } from '@/lib';
import { useNotification } from '@/hooks';
import {
	GET_CAMPAIGN_BY_ID,
	GET_PENDING_PAID_SCHEDULE_POSTS,
	GET_PENDING_SCHEDULE_POSTS,
	GET_SCHEDULE_POSTS,
	marketingService,
} from '@/api';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';

export type ChannelType = 'Ad' | 'Post' | 'Email';
interface BaseCardProps {
	type: ChannelType;
	disabled: boolean | undefined;
	tabsInfo?: CampaignTabs;
	isContentGenerated: boolean;
	optionsSelected: CampaignChannels[];
	setOptionsSelected: (value: CampaignChannels[]) => void;
}

interface CheckboxCardProps extends BaseCardProps {
	title: string;
	options: Option[];
}

interface CardProps extends BaseCardProps {
	id: string;
	item: Option;
	isOptionSelected: (name: Option) => boolean;
}

export const CheckboxCard: React.FC<CheckboxCardProps> = ({
	title,
	type,
	options,
	disabled,
	tabsInfo,
	isContentGenerated,
	optionsSelected,
	setOptionsSelected,
}) => {
	const isOptionSelected = (optionSelectedParam: Option): boolean => {
		return (
			optionsSelected.some((option) => option === optionSelectedParam.name) ||
			((optionSelectedParam.subOptions &&
				optionSelectedParam.subOptions?.some((item) =>
					optionsSelected.includes(item.name as CampaignChannels)
				)) ??
				false)
		);
	};

	return (
		<div>
			<Typography type="regular-16" className="text-black">
				{title}
			</Typography>

			<div className="flex flex-wrap gap-4 mt-2">
				{options.map((item, index) => (
					<React.Fragment key={'channel-option-' + index}>
						<Card
							id={'channel-option-card' + index}
							item={item}
							type={type}
							tabsInfo={tabsInfo}
							disabled={disabled || false}
							optionsSelected={optionsSelected}
							setOptionsSelected={setOptionsSelected}
							isOptionSelected={isOptionSelected}
							isContentGenerated={isContentGenerated}
						/>
					</React.Fragment>
				))}
			</div>
		</div>
	);
};

export const Card: React.FC<CardProps> = ({
	id,
	disabled,
	item,
	type,
	tabsInfo,
	isContentGenerated,
	optionsSelected,
	setOptionsSelected,
	isOptionSelected,
}) => {
	const { register, resetField } = useForm();
	const { toastError, toastSuccess } = useNotification();
	const [open, setOpen] = React.useState(false);
	const googleOptions = ['GOOGLE_SEARCH', 'GOOGLE_ADS'];

	const router = useRouter();
	const { id: campaignId } = router.query;

	const queryClient = useQueryClient();

	const allAds = getAllAdsArray(tabsInfo);

	const [isDeleteOption, setIsDeleteOption] = React.useState<Option[]>();

	React.useEffect(() => {
		if (!open && isDeleteOption) {
			setIsDeleteOption(undefined);
		}
	}, [open]);

	const handleAddOrRemove = (option: Option) => {
		const aux = [...optionsSelected];
		if (aux.some((item) => item === option.name)) {
			const auxFiltered = aux.filter((item) => item !== option.name);
			if (!googleOptions.includes(option.name) || !campaignId) {
				setOptionsSelected(auxFiltered);
			}

			// show text to delete if the channel has already been saved only
			if (allAds?.some((elem) => elem.type === option.name))
				setIsDeleteOption([...(isDeleteOption || []), option]);
		} else {
			aux.push(option.name as CampaignChannels);
			setOptionsSelected(aux);
			setIsDeleteOption(undefined);
		}
	};

	const handleDeleteAds = async (arrayTypes: Option[]) => {
		const aux = [...optionsSelected];
		const itemsToRemoved: CampaignChannels[] = [];

		arrayTypes.forEach(async (type) => {
			const idAd = allAds?.find((item) => item.type === type.name)?.id;
			if (!idAd) return;
			if (googleOptions.includes(type.name)) {
				itemsToRemoved.push(type.name as CampaignChannels);
			}
			try {
				await marketingService.deleteAds(idAd);
				toastSuccess(
					`The Advertising: ${
						campaignPlatformLabelMap[type.name as CampaignChannels]
					} channel has been removed.`
				);
				// wait some seg to refecth
				setTimeout(() => {
					queryClient.refetchQueries({ queryKey: [GET_CAMPAIGN_BY_ID] });
					queryClient.refetchQueries({ queryKey: [GET_SCHEDULE_POSTS] });
					queryClient.refetchQueries({
						queryKey: [GET_PENDING_SCHEDULE_POSTS],
					});
					queryClient.refetchQueries({
						queryKey: [GET_PENDING_PAID_SCHEDULE_POSTS],
					});
					resetField(type.name);
					resetUrl();
				}, 500);
			} catch (error) {
				toastError('An error occurred while removing. Please try again.');
			} finally {
				setIsDeleteOption(undefined);
				setOpen(false);
			}
		});

		if (itemsToRemoved.length > 0) {
			const filteredChannels = aux.filter(
				(item) => !itemsToRemoved.includes(item)
			);
			setOptionsSelected(filteredChannels);
		}
	};

	const resetUrl = async () => {
		const res = await marketingService.getCampaignById(campaignId as string);

		const campaignDetails = res.data;

		// the same order like the tabs priority
		const channels = [
			...campaignDetails.tabs.ads,
			...campaignDetails.tabs.posts,
			...campaignDetails.tabs.email,
		];

		const firstTab = channels[0].type.toLowerCase();

		router.push({
			pathname: AppRoutes.LAWYERAPP_MARKETING_CAMPAIGN + `/${campaignId}`,
			query: {
				tab: firstTab,
			},
		});
	};

	const adSaved = (option: Option) => {
		return allAds?.some(
			(elem) => elem.type === (option.name as CampaignChannels)
		);
	};

	return (
		<DropdownMenu
			modal={false}
			open={open}
			onOpenChange={() => {
				if (disabled) return;

				// You can't do anything if you have only one add selected and you have already created the content.
				if (
					allAds &&
					allAds.length === 1 &&
					!item.subOptions &&
					isContentGenerated
				)
					return;

				// Open the modal if to delete the ad -> if you have already saved the channels.
				if (isOptionSelected(item) && !!allAds && adSaved(item)) setOpen(!open);

				// Open the modal if you have sub-options and the content is not generated
				if (item.subOptions && item.subOptions.length > 0) setOpen(!open);

				if (!isContentGenerated && !item.subOptions && !adSaved(item))
					handleAddOrRemove(item);
			}}
		>
			<DropdownMenuTrigger id={id} className="w-full md:w-auto">
				<button
					disabled={disabled}
					type="button"
					className={clsx(
						'border p-3 w-full lg:w-[80px] xl:w-[125px] 2xl:w-[145px] h-[74px] rounded-lg',
						isOptionSelected(item)
							? 'border-primary bg-bg-white'
							: 'border-[#E1E4E5]',
						{
							'!border-[#0D061633]': disabled,
							'cursor-auto':
								(isContentGenerated && !isOptionSelected(item)) ||
								(allAds && allAds.length === 1 && isContentGenerated),
						}
					)}
				>
					<CardContent
						item={item}
						disabled={disabled || false}
						isOptionSelected={isOptionSelected}
					/>
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				className={clsx(
					'px-6 pb-4 pt-7 rounded-3xl',
					item.subOptions ? ' w-[266px]' : ' w-[224px]'
				)}
			>
				<DropdownMenuLabel className="space-y-6 px-0">
					{item.subOptions && item.subOptions.length > 0 ? (
						<>
							{item.subOptions?.map((subOption) => (
								<React.Fragment key={'card-subOption-' + subOption.name}>
									<div className="flex items-center gap-x-2">
										<InputCheck
											name={subOption.name}
											register={register}
											classNameInput="!bg-white"
											onChangeCustom={() => {
												handleAddOrRemove(subOption);
											}}
											defaultChecked={isOptionSelected(subOption)}
											disabled={
												(isContentGenerated &&
													!allAds?.some((ad) => ad.type === subOption.name)) ||
												(allAds?.length === 1 &&
													isOptionSelected(subOption) &&
													isContentGenerated)
											}
										/>
										<Icon src={subOption.icon || ''} className="!size-6" />
										<Typography type="regular-14" className="text-black">
											{subOption.label}
										</Typography>
									</div>
								</React.Fragment>
							))}
							{isDeleteOption && (
								<ConfirmDeleteCard
									type={type}
									handleDeleteAds={() => handleDeleteAds(isDeleteOption)}
								/>
							)}
						</>
					) : (
						<ConfirmDeleteCard
							type={type}
							handleDeleteAds={() => handleDeleteAds([item])}
						/>
					)}
				</DropdownMenuLabel>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

interface CardContentProps {
	item: Option;
	disabled: boolean;
	isOptionSelected: (name: Option) => boolean;
}

export const CardContent: React.FC<CardContentProps> = ({
	item,
	disabled,
	isOptionSelected,
}) => {
	return (
		<div>
			{item.icon && (
				<Icon
					src={item.icon}
					className={clsx('!size-8 mx-auto', item.classNameItemIcon)}
				/>
			)}
			<Typography
				type="semibold-12"
				className={clsx(
					isOptionSelected(item) ? 'text-primary' : 'text-[#183A5B]',
					{
						'!text-gray-35': disabled,
					}
				)}
			>
				{item.label}
			</Typography>
		</div>
	);
};

export const ConfirmDeleteCard: React.FC<{
	type: ChannelType;
	handleDeleteAds: () => void;
}> = ({ type, handleDeleteAds }) => {
	return (
		<div className="flex flex-col items-center justify-center w-full">
			<Typography type="regular-16" className="text-black text-center mb-6">
				Confirm deletion of the {type}.
			</Typography>
			<Button
				size="fit"
				decoration="primary"
				label="Confirm"
				className="!py-0.5"
				onClick={handleDeleteAds}
			/>
		</div>
	);
};
